import { useEffect, useRef, useState } from 'react';
import { ChevronRightIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsiblePanel,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type {
  LibraryItemView,
  TakeDownPiece,
  TakeDownResult,
} from '@/lib/api/generated/model';

import { useTakeDownSet } from '../hooks/useTakeDownSet';
import { alsoLine, stayingLabel } from '../takeDownWords';

const KIND_ORDER = { workflow: 0, agent: 1, skill: 2 } as const;

const idOf = (piece: { kind: string; key: string }) =>
  `${piece.kind}:${piece.key}`;

/**
 * The take-down confirmation, offering the set (spec 213).
 *
 * The service says which linked pieces the same person shared would go with
 * the item, because nothing else in the Library uses them, and which stay and
 * why. They are offered ticked; each can be kept. What stays is listed, folded,
 * so the person can see nothing in use is taken. Inline in the drawer, not a
 * dialog over it (spec 206).
 */
export function TakeDownConfirm({
  workspaceId,
  item,
  sharer,
  onCancel,
  onTakenDown,
}: {
  workspaceId: string;
  item: LibraryItemView;
  /** Who shared it, or ``null`` when it was the person reading. */
  sharer: string | null;
  onCancel: () => void;
  onTakenDown: (result: TakeDownResult) => void;
}) {
  const remover = useTakeDownSet(workspaceId, item, true);
  // What the person unticked; everything else offered goes, so a piece the
  // service offers after a re-check starts ticked (research §4).
  const [unticked, setUnticked] = useState<ReadonlySet<string>>(new Set());
  // Workflows first, as the line counts them.
  const also = [...(remover.offer?.also ?? [])].sort(
    (a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind],
  );
  const staying = remover.offer?.staying ?? [];
  const chosen = also.filter((piece) => !unticked.has(idOf(piece)));

  const toggle = (piece: TakeDownPiece, on: boolean) => {
    const next = new Set(unticked);
    if (on) next.delete(idOf(piece));
    else next.add(idOf(piece));
    setUnticked(next);
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm">
        Take {item.name} out of the Library? Workspaces that installed it keep
        their copies.
      </p>
      <p role="status" className="text-muted-foreground min-h-4 text-xs">
        {remover.checking ? 'Checking what goes with it…' : ''}
      </p>
      {remover.checkFailed && !remover.offer && (
        <p className="text-muted-foreground text-sm">
          What goes with it could not be checked.
        </p>
      )}
      {also.length > 0 && (
        <SetChoice
          also={also}
          chosen={chosen}
          sharer={sharer}
          onAll={(on) => setUnticked(on ? new Set() : new Set(also.map(idOf)))}
          onOne={toggle}
        />
      )}
      {staying.length > 0 && (
        <Collapsible className="flex flex-col gap-1">
          <CollapsibleTrigger className="hover:bg-muted/50 group touch-target flex items-center gap-2 rounded-md px-2 py-2 text-left text-sm">
            <ChevronRightIcon
              aria-hidden="true"
              className="text-muted-foreground size-4 shrink-0 transition-transform duration-[var(--motion-duration-base)] group-data-panel-open:rotate-90"
            />
            {stayingLabel(staying.length)}
          </CollapsibleTrigger>
          <CollapsiblePanel>
            <ul className="flex flex-col gap-1 pl-8 text-sm">
              {staying.map((piece) => (
                <li key={idOf(piece)} className="flex flex-col">
                  <span>
                    {piece.name}{' '}
                    <span className="text-muted-foreground text-xs">
                      · {piece.kind}
                    </span>
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {piece.reason}
                  </span>
                </li>
              ))}
            </ul>
          </CollapsiblePanel>
        </Collapsible>
      )}
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={remover.pending}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="destructive"
          size="sm"
          disabled={remover.pending || remover.checking}
          onClick={() =>
            remover.takeDown(
              chosen.map(({ kind, key }) => ({ kind, key })),
              onTakenDown,
            )
          }
        >
          {remover.checkFailed && !remover.offer
            ? 'Take down only this'
            : 'Take down'}
        </Button>
      </div>
      {remover.error && (
        <p role="alert" className="text-destructive text-sm">
          {remover.error}
        </p>
      )}
    </div>
  );
}

/**
 * The set's own tick, all, some (mixed) or none, and a tick per piece. Rows are
 * sized by padding, so the 44px floor under a coarse pointer holds.
 */
function SetChoice({
  also,
  chosen,
  sharer,
  onAll,
  onOne,
}: {
  also: readonly TakeDownPiece[];
  chosen: readonly TakeDownPiece[];
  sharer: string | null;
  onAll: (on: boolean) => void;
  onOne: (piece: TakeDownPiece, on: boolean) => void;
}) {
  const all = useRef<HTMLInputElement>(null);
  const mixed = chosen.length > 0 && chosen.length < also.length;
  useEffect(() => {
    if (all.current) all.current.indeterminate = mixed;
  }, [mixed]);
  const ticked = new Set(chosen.map(idOf));
  return (
    <div className="flex flex-col gap-0.5">
      <label className="hover:bg-muted/50 touch-target flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm font-medium">
        <input
          ref={all}
          type="checkbox"
          className="accent-primary"
          checked={chosen.length === also.length}
          aria-checked={mixed ? 'mixed' : chosen.length === also.length}
          onChange={(event) => onAll(event.target.checked)}
        />
        {alsoLine(chosen.length > 0 ? chosen : also, sharer)}
      </label>
      <ul className="flex flex-col gap-0.5 pl-6">
        {also.map((piece) => (
          <li key={idOf(piece)}>
            <label className="hover:bg-muted/50 touch-target flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm">
              <input
                type="checkbox"
                className="accent-primary"
                checked={ticked.has(idOf(piece))}
                onChange={(event) => onOne(piece, event.target.checked)}
              />
              <span className="truncate">{piece.name}</span>
              <span className="text-muted-foreground shrink-0 text-xs">
                · {piece.kind}
              </span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}
